import type { AgentToolDescriptor } from "./agentTypes";
import type { RefusalReason } from "./tools";
import { currentValueAt, sameCriterion } from "./confirmationCardFormatting";

type Row = Record<string, unknown>;
type Claim = { path: string; source: Row & { kind: string } };

const USER_ANSWER = "USER_ANSWER";
const ASSISTANT_SUGGESTION = "ASSISTANT_SUGGESTION";
const FIELD_SOURCES = "fieldSources";
const NUMBER = /(?<![\p{L}\d])[-−]?\d+(?:[.,]\d+)?/gu;
const PERCENTAGE = /[-−+]?\d+(?:[.,]\d+)?\s*(?:%|prosent|percent)/gu;
const ABSOLUTE_PERCENTAGE =
  /^\p{L}*\s*([:=]|(?:(?:ned|opp|down|up)\s+)?(?:til|to)|på|at|skal\s+være|blir|be|is)?\s*([-−+]?\d+(?:[.,]\d+)?)\s*(?:%|prosent|percent)(?![\p{L}\d])(?!\s*(?:hver|each|poeng|points))/u;
const NEGATION = /(?<![\p{L}])(?:ikke|aldri|not|never|don't|do not)(?![\p{L}])/u;
const CLAUSE_BREAK = /[.!?;,]\s*/gu;
const MIN_WEIGHT = 1;
const MAX_WEIGHT = 100;
const CRITERIA_PATH = ["eventData", "awardCriteria", "bestPriceQualityRatio", "criteria"];

export type WeightDeparture = {
  index: number;
  label: string;
  proposed: number;
  reason: "outOfRange" | "sumNot100" | null;
} & ({ requested: number } | { stored: number });

export const withHonestUserSources = (
  descriptor: AgentToolDescriptor,
  args: Row,
  userWords: string,
  departures: WeightDeparture[] = [],
): Row => {
  const root = claimRoot(descriptor, args);
  const claims = root?.[FIELD_SOURCES];
  if (!root || !Array.isArray(claims)) return args;
  const said = numbersIn(userWords);
  const departed = departures.map(({ index }) => [...CRITERIA_PATH, String(index), "weight", "number"]);
  const honest = claims.map((claim) =>
    isUserClaim(claim) &&
    (weightsAt(root, claim.path).some((weight) => !said.has(weight)) ||
      departed.some((path) => overlaps(segmentsOf(claim.path), path)))
      ? { ...claim, source: { kind: ASSISTANT_SUGGESTION } }
      : claim,
  );
  return replaceRoot(descriptor, args, { ...root, [FIELD_SOURCES]: honest });
};

export const weightDepartures = (
  descriptor: AgentToolDescriptor,
  args: Row,
  request: string,
  labelOf: (criterion: Row) => string,
  current?: Row,
): WeightDeparture[] => {
  const criteria = valueAt(bodyOf(descriptor, args), CRITERIA_PATH);
  if (!Array.isArray(criteria)) return [];
  const rows = criteria.map((criterion) => (isRow(criterion) ? criterion : {}));
  const labels = rows.map(labelOf);
  const proposed = rows.map(percentageOf);
  const before = currentWeights(rows, current);
  const { requested, understood } = requestedWeights(request, labels);
  const asRequested = proposed.map((weight, index) => requested.get(index) ?? before[index] ?? weight);
  const requestedSum = asRequested.every((weight) => weight !== null) ? roundedSum(asRequested as number[]) : null;
  const sumReason: WeightDeparture["reason"] =
    understood && requested.size > 0 && requestedSum !== null && requestedSum !== 100 ? "sumNot100" : null;
  return proposed.flatMap((weight, index): WeightDeparture[] => {
    if (weight === null) return [];
    const label = labels[index];
    const asked = requested.get(index);
    if (asked !== undefined && (understood || sameNumber(weight, asked))) {
      if (sameNumber(weight, asked)) return [];
      const reason = asked < MIN_WEIGHT || asked > MAX_WEIGHT ? "outOfRange" : sumReason;
      return [{ index, label, requested: asked, proposed: weight, reason }];
    }
    const was = before[index];
    if (was === null || sameNumber(weight, was)) return [];
    return [{ index, label, stored: was, proposed: weight, reason: sumReason }];
  });
};

const currentWeights = (rows: Row[], current: Row | undefined): (number | null)[] => {
  const stored = currentValueAt(current, CRITERIA_PATH);
  const storedRows = Array.isArray(stored) ? stored.filter(isRow) : [];
  return rows.map((row) => {
    const match = storedRows.find((candidate) => sameCriterion(candidate, row));
    return match ? percentageOf(match) : null;
  });
};

export const refusedUserValues = (
  descriptor: AgentToolDescriptor,
  args: Row,
  reasons: RefusalReason[],
): RefusalReason[] => {
  const root = claimRoot(descriptor, args);
  const claims = root?.[FIELD_SOURCES];
  if (!Array.isArray(claims)) return [];
  const userPaths = claims.filter(isUserClaim).map((claim) => dotted(claim.path));
  return reasons.filter(
    ({ field }) => field !== undefined && userPaths.some((path) => field.startsWith(path) || path.startsWith(field)),
  );
};

export const weightSumOf = (descriptor: AgentToolDescriptor, args: Row): number | null => {
  const weights = percentageWeightsOf(descriptor, args);
  return weights === null ? null : roundedSum(weights.map(({ weight }) => weight));
};

export const percentageWeightsOf = (
  descriptor: AgentToolDescriptor,
  args: Row,
): { criterion: Row; weight: number }[] | null => {
  const criteria = valueAt(bodyOf(descriptor, args), CRITERIA_PATH);
  if (!Array.isArray(criteria) || criteria.length === 0) return null;
  const rows = criteria.map((criterion) => (isRow(criterion) ? criterion : {}));
  const weights = rows.map(percentageOf);
  if (weights.some((weight) => weight === null)) return null;
  return rows.map((criterion, index) => ({ criterion, weight: weights[index] as number }));
};

export type SubCriterionWeightGap = { criterion: Row; unweighted: number; sum: number };

export const subCriterionWeightGapsOf = (descriptor: AgentToolDescriptor, args: Row): SubCriterionWeightGap[] =>
  (percentageWeightsOf(descriptor, args) ?? []).flatMap(({ criterion }) => {
    const subWeights = (Array.isArray(criterion.subCriteria) ? criterion.subCriteria : []).filter(isRow).map(percentageOf);
    if (subWeights.length === 0) return [];
    const unweighted = subWeights.filter((subWeight) => subWeight === null).length;
    const sum = roundedSum(subWeights.filter((subWeight): subWeight is number => subWeight !== null));
    return unweighted === 0 && sameNumber(sum, 100) ? [] : [{ criterion, unweighted, sum }];
  });

export const weightPartsOf = (descriptor: AgentToolDescriptor, args: Row): number[] =>
  (percentageWeightsOf(descriptor, args) ?? []).flatMap(({ criterion, weight }) => {
    const subWeights = (Array.isArray(criterion.subCriteria) ? criterion.subCriteria : [])
      .filter(isRow)
      .map(percentageOf)
      .filter((subWeight): subWeight is number => subWeight !== null);
    return [weight, ...subWeights, ...subWeights.map((subWeight) => Math.round((weight * subWeight) / 10) / 10)];
  });

const percentageOf = (criterion: Row): number | null =>
  isRow(criterion.weight) && criterion.weight.type === "PERCENTAGE" && typeof criterion.weight.number === "number"
    ? criterion.weight.number
    : null;

const roundedSum = (weights: number[]): number =>
  Math.round(weights.reduce((sum, weight) => sum + weight, 0) * 1000) / 1000;

const sameNumber = (a: number, b: number): boolean => Math.abs(a - b) < 0.0005;

const requestedWeights = (request: string, labels: string[]): { requested: Map<number, number>; understood: boolean } => {
  const text = request.toLocaleLowerCase("nb");
  const mentions = labels
    .flatMap((label, index) => aliasesOf(label, labels).map((alias) => ({ alias, index })))
    .flatMap(({ alias, index }) =>
      [...text.matchAll(new RegExp(`(?<![\\p{L}\\d])${escaped(alias)}`, "gu"))].map((match) => ({
        index,
        start: match.index ?? 0,
        end: (match.index ?? 0) + match[0].length,
      })),
    )
    .sort((a, b) => a.start - b.start || b.end - a.end)
    .filter((mention, position, all) => all.slice(0, position).every((earlier) => earlier.end <= mention.start));
  const requested = new Map<number, number>();
  const ambiguous = new Set<number>();
  mentions.forEach((mention, position) => {
    const until = mentions[position + 1]?.start ?? text.length;
    const found = text.slice(mention.end, until).match(ABSOLUTE_PERCENTAGE);
    const clauseStart = Math.max(mentions[position - 1]?.end ?? 0, lastClauseBreak(text, mention.start));
    if (found && NEGATION.test(text.slice(clauseStart, mention.start))) {
      ambiguous.add(mention.index);
      return;
    }
    if (!found || found[2].startsWith("+") || (/^[-−]/u.test(found[2]) && found[1] === undefined)) return;
    const number = numberOf(found[2]);
    const earlier = requested.get(mention.index);
    if (earlier !== undefined && !sameNumber(earlier, number)) ambiguous.add(mention.index);
    requested.set(mention.index, number);
  });
  ambiguous.forEach((index) => requested.delete(index));
  const percentages = text.match(PERCENTAGE)?.length ?? 0;
  const understood = ambiguous.size === 0 && percentages === requested.size;
  return { requested, understood };
};

const lastClauseBreak = (text: string, before: number): number => {
  const breaks = [...text.slice(0, before).matchAll(CLAUSE_BREAK)];
  const last = breaks[breaks.length - 1];
  return last ? (last.index ?? 0) + last[0].length : 0;
};

const aliasesOf = (label: string, labels: string[]): string[] => {
  const normalised = (value: string) => value.trim().toLocaleLowerCase("nb");
  const firstWord = (value: string) => normalised(value).split(/\s+/)[0];
  const full = normalised(label);
  const first = firstWord(label);
  const unique = (matches: (other: string) => boolean) => labels.filter(matches).length === 1;
  return [
    ...(full && unique((other) => normalised(other) === full) ? [full] : []),
    ...(first !== full && first.length >= 3 && unique((other) => firstWord(other) === first) ? [first] : []),
  ];
};

const escaped = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const overlaps = (a: string[], b: string[]): boolean => {
  const shorter = a.length <= b.length ? a : b;
  const longer = shorter === a ? b : a;
  return shorter.every((segment, index) => longer[index] === segment);
};

const isUserClaim = (claim: unknown): claim is Claim =>
  isRow(claim) && typeof claim.path === "string" && isRow(claim.source) && claim.source.kind === USER_ANSWER;

const numbersIn = (text: string): Set<number> =>
  new Set((text.match(NUMBER) ?? []).map(numberOf));

const numberOf = (text: string): number => Number(text.replace("−", "-").replace(",", "."));

const weightsAt = (root: Row, path: string): number[] => {
  const segments = segmentsOf(path);
  return weightNumbers(valueAt(root, segments), segments);
};

const weightNumbers = (node: unknown, trail: string[]): number[] => {
  if (typeof node === "number") return trail.includes("weight") ? [node] : [];
  if (Array.isArray(node)) return node.flatMap((item, index) => weightNumbers(item, [...trail, String(index)]));
  if (isRow(node)) return Object.entries(node).flatMap(([key, child]) => weightNumbers(child, [...trail, key]));
  return [];
};

const valueAt = (root: unknown, segments: string[]): unknown =>
  segments.reduce<unknown>(
    (node, key) => (Array.isArray(node) ? node[Number(key)] : isRow(node) ? node[key] : undefined),
    root,
  );

const segmentsOf = (path: string): string[] => dotted(path).split(".");

const dotted = (path: string): string => path.replace(/\[(\d+)\]/g, ".$1");

const bodyName = (descriptor: AgentToolDescriptor): string | undefined =>
  descriptor.params.find((param) => param.in === "body")?.name;

const bodyOf = (descriptor: AgentToolDescriptor, args: Row): Row => {
  const name = bodyName(descriptor);
  const body = name ? args[name] : undefined;
  return isRow(body) ? body : args;
};

const claimRoot = (descriptor: AgentToolDescriptor, args: Row): Row | null => {
  const body = bodyOf(descriptor, args);
  if (Array.isArray(body[FIELD_SOURCES])) return body;
  return Array.isArray(args[FIELD_SOURCES]) ? args : null;
};

const replaceRoot = (descriptor: AgentToolDescriptor, args: Row, root: Row): Row => {
  const name = bodyName(descriptor);
  return name && isRow(args[name]) && Array.isArray((args[name] as Row)[FIELD_SOURCES])
    ? { ...args, [name]: root }
    : root;
};

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
